import { ArrowRight, FileText, Info, FolderPlus, Layers, ScanSearch, UserPlus, Users, UserX } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type Column, type Sort } from '@/components/data/DataTable'
import { DetailPane, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { LoadingRows, SearchInput } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { StatusText } from '@/components/data/StatusText'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { useCachedList } from '@/lib/useCachedList'
import { useSelection } from '@/lib/useSelection'

type User = { account_id: string; email: string | null; display_name: string; active: boolean; team: string | null; source?: 'cache' | 'live' }

/** Shortcuts to user operations — they open the operation page. */
const TOOLS = [
  { label: 'Invite user', icon: UserPlus, op: 'users.invite_org_user' },
  { label: 'Find inactive', icon: UserX, op: 'users.find_inactive_users' },
  { label: 'Bulk assign groups', icon: Layers, op: 'users.bulk_assign_groups' },
  { label: 'Groups', icon: Users, op: 'users.find_groups' },
  { label: 'Create group', icon: FolderPlus, op: 'users.create_group' },
  { label: 'License report', icon: FileText, op: 'users.license_report' },
]

function sortUsers(users: User[], sort: Sort): User[] {
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...users].sort((a, b) => {
    if (sort.key === 'active') return ((b.active ? 1 : 0) - (a.active ? 1 : 0)) * dir
    const key = sort.key as 'display_name' | 'email' | 'team'
    // empty values sort last whatever the direction
    const va = a[key] ?? ''
    const vb = b[key] ?? ''
    if (!va || !vb) return va ? -1 : vb ? 1 : 0
    return va.localeCompare(vb, undefined, { sensitivity: 'base' }) * dir
  })
}

const status = (u: User) => (u.active ? 'active' : 'inactive')

const COLUMNS: Column<User>[] = [
  { key: 'active', header: 'Status', width: '104px', sortable: true, render: (u) => <StatusText status={status(u)} />, title: status },
  { key: 'display_name', header: 'Name', sortable: true },
  { key: 'team', header: 'Team', width: '16%', sortable: true, render: (u) => u.team || '—' },
  { key: 'email', header: 'Email', mono: true, width: '26%', sortable: true, render: (u) => u.email ?? '—' },
  { key: 'account_id', header: 'Account ID', mono: true, width: '24%' },
]

function UserSummary({ user }: { user: User }) {
  return (
    <DetailPane
      eyebrow="User"
      title={user.display_name}
      status={<StatusText status={status(user)} />}
      actions={(
        <>
          <Link to={`/users/${encodeURIComponent(user.account_id)}`} state={{ user }} className={detailActionClass}>
            Open user <ArrowRight aria-hidden className="size-3.5" />
          </Link>
          <Link to={`/analysis/user/${encodeURIComponent(user.account_id)}`} className={detailActionClass}>
            <ScanSearch aria-hidden className="size-3.5" /> Where used
          </Link>
        </>
      )}
    >
      <KeyValue
        items={[
          ['Email', <span className="font-mono">{user.email ?? '—'}</span>],
          ['Team', user.team || '—'],
          ['Account ID', <span className="font-mono text-[12px] text-text-2">{user.account_id}</span>],
        ]}
      />
    </DetailPane>
  )
}

export default function UsersList() {
  const [q, setQ] = useState('')
  // live=1: on a cache miss the backend asks Jira directly (only this page opts in)
  const users = useCachedList<User>('/users', q, 300, 'live=1')
  const [sort, setSort] = useState<Sort>({ key: 'display_name', dir: 'asc' })
  const [sel, setSel] = useSelection()

  const rows = useMemo(() => (Array.isArray(users) ? sortUsers(users, sort) : []), [users, sort])
  const selected = rows.find((u) => u.account_id === sel) ?? null
  const onSort = (key: string) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }))

  return (
    <Page
      title="Users"
      hint="Search the directory, view groups and status, manage access."
      context={<SearchInput value={q} onChange={setQ} placeholder="Search name or email" label="Search users" />}
      freshness={Array.isArray(users) ? <span className="font-mono">{users.length} users</span> : undefined}
    >
      <FilterBar>
        <span className="text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">Tools</span>
        {TOOLS.map(({ label, icon: Icon, op }) => (
          <Link key={op} to={`/operations/${op}`} className={detailActionClass}>
            <Icon aria-hidden className="size-3.5" />
            {label}
          </Link>
        ))}
      </FilterBar>
      <SectionBoundary label="Users" resetKeys={[users]}>
        <SplitView
          onCloseDetail={() => setSel(null)}
          detail={selected && <UserSummary user={selected} />}
          list={users === 'error'
            ? (
              <p role="alert" className="px-4 py-4 text-[13px] text-danger">
                Couldn’t load users — the cache answered something unexpected. Sync from Jira in Settings → Jira cache.
              </p>
            )
            : users === null
              ? <LoadingRows label="Loading users" />
              : (
                <>
                  {rows.some((u) => u.source === 'live') && (
                    <p role="status" className="flex items-center gap-1.5 border-b border-line px-4 py-1.5 text-[12px] text-text-2">
                      <Info aria-hidden className="size-3.5 shrink-0 text-text-3" />
                      Not in cache yet — found in Jira
                    </p>
                  )}
                  <DataTable
                  label="Users"
                  rows={rows}
                  columns={COLUMNS}
                  rowId={(u) => u.account_id}
                  selectedId={sel}
                  onSelect={setSel}
                  sort={sort}
                  onSort={onSort}
                  empty={q ? 'No users match that query.' : 'No users — Sync from Jira in Settings → Jira cache.'}
                  />
                </>
              )}
        />
      </SectionBoundary>
    </Page>
  )
}

